import { useEffect, useState } from 'react';
import { Button, DatePickerSelect } from '@round/ui-kit';
import { showNotification } from 'helpers';
import FormField from 'ui/DataEntry/FormField/FormField';
import Label from 'ui/DataEntry/Label/Label';
import Modal, { ModalContent, ModalFooter, ModalTitle } from 'ui/General/Modal/Modal';
import styles from './RetainerMonthModal.module.css';

type Props = {
    isOpen: boolean;
    retainerMonth: string | null | undefined;
    onClose: () => void;
    // Resolves to whether the save succeeded
    onSave: (retainerMonth: string | null) => Promise<boolean>;
};

const RetainerMonthModal = ({ isOpen, retainerMonth, onClose, onSave }: Props) => {
    const [month, setMonth] = useState<Date | null>(null);
    const [pending, setPending] = useState<'save' | 'clear' | null>(null);
    const isSaving = pending !== null;

    useEffect(() => {
        if (isOpen) {
            setMonth(parseRetainerMonth(retainerMonth));
        }
    }, [isOpen, retainerMonth]);

    const handleSave = async (value: string | null) => {
        setPending(value === null ? 'clear' : 'save');
        const saved = await onSave(value).catch(() => false);
        setPending(null);
        if (!saved) {
            showNotification('Could not update the retainer', 'error');
            return;
        }
        onClose();
    };

    // Closing mid-save would let a reopened dialog be closed by the earlier save finishing
    const handleClose = () => {
        if (isSaving) {
            return;
        }
        onClose();
    };

    return (
        <Modal isOpen={isOpen} closeOnOverlayClick onClose={handleClose}>
            <ModalTitle>Retainer</ModalTitle>
            <ModalContent>
                <FormField>
                    <Label>Month this retainer covers</Label>
                    <DatePickerSelect
                        className={styles.monthSelect}
                        placeholder="Select month..."
                        maxDetail="year"
                        minDetail="year"
                        closeOnChange
                        value={month}
                        formatDisplayDate={getMonthLabel}
                        onChange={(value: Date) => {
                            // A popover left open must not change the month while it saves
                            if (isSaving) {
                                return;
                            }
                            setMonth(value);
                        }}
                        onClear={() => {
                            if (isSaving) {
                                return;
                            }
                            setMonth(null);
                        }}
                    />
                </FormField>
            </ModalContent>
            <ModalFooter align="right">
                {retainerMonth && (
                    <Button
                        type="bordered"
                        color="negative"
                        loading={pending === 'clear'}
                        disabled={pending === 'save'}
                        onClick={() => handleSave(null)}
                    >
                        Not a retainer
                    </Button>
                )}
                <Button disabled={isSaving} onClick={handleClose}>
                    Cancel
                </Button>
                <Button
                    type="filled"
                    color="black"
                    loading={pending === 'save'}
                    disabled={!month || pending === 'clear'}
                    onClick={() => {
                        if (month) {
                            return handleSave(formatRetainerMonth(month));
                        }
                    }}
                >
                    Save
                </Button>
            </ModalFooter>
        </Modal>
    );
};

const parseRetainerMonth = (value: string | null | undefined): Date | null => {
    if (!value) {
        return null;
    }

    const [year, month, day] = value.split('-').map(Number);
    if (!year || !month) {
        return null;
    }

    return new Date(year, month - 1, day || 1);
};

const getMonthLabel = (value: Date | [Date] | [Date, Date] | null | undefined) =>
    value instanceof Date ? value.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' }) : '';

const formatRetainerMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}-01`;
};

export default RetainerMonthModal;
